import React, { useState, useEffect } from "react";
import { database } from "../../firebaseConfig";
import { ref, get, set } from "firebase/database";
import "./CardConfigEditor.css";

// Define default configuration with nested objects and order values
const defaultConfig = {
    cardBgColor: "#ffffff",
    title: {
        fontSize: "1.2rem",
        align: "left",
        color: "#000000",
        order: 1,
    },
    description: {
        fontSize: "1rem",
        align: "left",
        color: "#555555",
        order: 2,
    },
    price: {
        fontSize: "1rem",
        align: "right",
        color: "#000000",
        order: 3,
    },
    discount: {
        fontSize: "1rem",
        align: "right",
        color: "red",
        order: 4,
    },
    rating: {
        fontSize: "0.9rem",
        align: "right",
        color: "#888888",
        order: 5,
    },
};

const mergeConfig = (fetched) => {
    // Merge fetched config with defaultConfig so that missing nested objects get default values.
    return {
        cardBgColor: fetched?.cardBgColor || defaultConfig.cardBgColor,
        title: {
            fontSize: fetched?.title?.fontSize || defaultConfig.title.fontSize,
            align: fetched?.title?.align || defaultConfig.title.align,
            color: fetched?.title?.color || defaultConfig.title.color,
            order: fetched?.title?.order || defaultConfig.title.order,
        },
        description: {
            fontSize: fetched?.description?.fontSize || defaultConfig.description.fontSize,
            align: fetched?.description?.align || defaultConfig.description.align,
            color: fetched?.description?.color || defaultConfig.description.color,
            order: fetched?.description?.order || defaultConfig.description.order,
        },
        price: {
            fontSize: fetched?.price?.fontSize || defaultConfig.price.fontSize,
            align: fetched?.price?.align || defaultConfig.price.align,
            color: fetched?.price?.color || defaultConfig.price.color,
            order: fetched?.price?.order || defaultConfig.price.order,
        },
        discount: {
            fontSize: fetched?.discount?.fontSize || defaultConfig.discount.fontSize,
            align: fetched?.discount?.align || defaultConfig.discount.align,
            color: fetched?.discount?.color || defaultConfig.discount.color,
            order: fetched?.discount?.order || defaultConfig.discount.order,
        },
        rating: {
            fontSize: fetched?.rating?.fontSize || defaultConfig.rating.fontSize,
            align: fetched?.rating?.align || defaultConfig.rating.align,
            color: fetched?.rating?.color || defaultConfig.rating.color,
            order: fetched?.rating?.order || defaultConfig.rating.order,
        },
    };
};

const CardConfigEditor = () => {
    const [config, setConfig] = useState(defaultConfig);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Fetch global card config from Firebase (stored at the 'cardConfig' node)
        const fetchConfig = async () => {
            try {
                const snapshot = await get(ref(database, "cardConfig"));
                if (snapshot.exists()) {
                    const fetched = snapshot.val();
                    setConfig(mergeConfig(fetched));
                } else {
                    setConfig(defaultConfig);
                }
            } catch (error) {
                console.error("Error fetching card config:", error);
                setConfig(defaultConfig);
            }
            setLoading(false);
        };
        fetchConfig();
    }, []);

    const handleSave = async () => {
        try {
            await set(ref(database, "cardConfig"), config);
            alert("Card configuration saved.");
        } catch (error) {
            console.error("Error saving card config:", error);
            alert("Error saving configuration.");
        }
    };

    if (loading) return <div>Loading configuration...</div>;

    // Build an array of fields based on config and sort by order
    const fields = [
        {
            key: "title",
            order: config.title.order,
            component: (
                <h3
                    className="dummy-title"
                    style={{
                        fontSize: config.title.fontSize,
                        textAlign: config.title.align,
                        color: config.title.color,
                    }}
                >
                    Dummy Title
                </h3>
            ),
        },
        {
            key: "description",
            order: config.description.order,
            component: (
                <p
                    className="dummy-description"
                    style={{
                        fontSize: config.description.fontSize,
                        textAlign: config.description.align,
                        color: config.description.color,
                    }}
                >
                    Dummy Description
                </p>
            ),
        },
        {
            key: "price",
            order: config.price.order,
            component: (
                <span
                    className="dummy-price"
                    style={{
                        fontSize: config.price.fontSize,
                        textAlign: config.price.align,
                        color: config.price.color,
                    }}
                >
                    $99.99
                </span>
            ),
        },
        {
            key: "discount",
            order: config.discount.order,
            component: (
                <span
                    className="dummy-discount"
                    style={{
                        fontSize: config.discount.fontSize,
                        textAlign: config.discount.align,
                        color: config.discount.color,
                    }}
                >
                    10% Off
                </span>
            ),
        },
        {
            key: "rating",
            order: config.rating.order,
            component: (
                <p
                    className="dummy-rating"
                    style={{
                        fontSize: config.rating.fontSize,
                        textAlign: config.rating.align,
                        color: config.rating.color,
                    }}
                >
                    Rating: 4.5
                </p>
            ),
        },
    ];

    // Sort fields by order
    fields.sort((a, b) => a.order - b.order);

    return (
        <div className="card-config-editor">
            <h2>Edit Card Configuration</h2>
            <div className="config-container">
                {/* Dummy Card Preview */}
                <div className="dummy-card" style={{ backgroundColor: config.cardBgColor }}>
                    <div className="dummy-media-container">
                        <img
                            src="https://www.shutterstock.com/shutterstock/photos/731484199/display_1500/stock-photo-dummy-pills-not-medicines-731484199.jpg"
                            alt="Dummy"
                            className="dummy-media"
                        />
                    </div>
                    <div className="dummy-details">
                        {fields.map((field) => (
                            <React.Fragment key={field.key}>{field.component}</React.Fragment>
                        ))}
                    </div>
                </div>
                {/* Appearance Controls */}
                <div className="config-controls">
                    <label>
                        Card Background:
                        <input
                            type="color"
                            value={config.cardBgColor}
                            onChange={(e) =>
                                setConfig({ ...config, cardBgColor: e.target.value })
                            }
                        />
                    </label>
                    <fieldset>
                        <legend>Title Settings</legend>
                        <label>
                            Font Size:
                            <input
                                type="text"
                                value={config.title.fontSize}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        title: { ...config.title, fontSize: e.target.value },
                                    })
                                }
                            />
                        </label>
                        <label>
                            Alignment:
                            <select
                                value={config.title.align}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        title: { ...config.title, align: e.target.value },
                                    })
                                }
                            >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                            </select>
                        </label>
                        <label>
                            Color:
                            <input
                                type="color"
                                value={config.title.color}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        title: { ...config.title, color: e.target.value },
                                    })
                                }
                            />
                        </label>
                        <label>
                            Order:
                            <input
                                type="number"
                                value={config.title.order}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        title: { ...config.title, order: Number(e.target.value) },
                                    })
                                }
                            />
                        </label>
                    </fieldset>
                    <fieldset>
                        <legend>Description Settings</legend>
                        <label>
                            Font Size:
                            <input
                                type="text"
                                value={config.description.fontSize}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        description: { ...config.description, fontSize: e.target.value },
                                    })
                                }
                            />
                        </label>
                        <label>
                            Alignment:
                            <select
                                value={config.description.align}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        description: { ...config.description, align: e.target.value },
                                    })
                                }
                            >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                            </select>
                        </label>
                        <label>
                            Color:
                            <input
                                type="color"
                                value={config.description.color}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        description: { ...config.description, color: e.target.value },
                                    })
                                }
                            />
                        </label>
                        <label>
                            Order:
                            <input
                                type="number"
                                value={config.description.order}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        description: { ...config.description, order: Number(e.target.value) },
                                    })
                                }
                            />
                        </label>
                    </fieldset>
                    <fieldset>
                        <legend>Price Settings</legend>
                        <label>
                            Font Size:
                            <input
                                type="text"
                                value={config.price.fontSize}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        price: { ...config.price, fontSize: e.target.value },
                                    })
                                }
                            />
                        </label>
                        <label>
                            Alignment:
                            <select
                                value={config.price.align}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        price: { ...config.price, align: e.target.value },
                                    })
                                }
                            >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                            </select>
                        </label>
                        <label>
                            Color:
                            <input
                                type="color"
                                value={config.price.color}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        price: { ...config.price, color: e.target.value },
                                    })
                                }
                            />
                        </label>
                        <label>
                            Order:
                            <input
                                type="number"
                                value={config.price.order}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        price: { ...config.price, order: Number(e.target.value) },
                                    })
                                }
                            />
                        </label>
                    </fieldset>
                    <fieldset>
                        <legend>Discount Settings</legend>
                        <label>
                            Font Size:
                            <input
                                type="text"
                                value={config.discount.fontSize}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        discount: { ...config.discount, fontSize: e.target.value },
                                    })
                                }
                            />
                        </label>
                        <label>
                            Alignment:
                            <select
                                value={config.discount.align}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        discount: { ...config.discount, align: e.target.value },
                                    })
                                }
                            >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                            </select>
                        </label>
                        <label>
                            Color:
                            <input
                                type="color"
                                value={config.discount.color}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        discount: { ...config.discount, color: e.target.value },
                                    })
                                }
                            />
                        </label>
                        <label>
                            Order:
                            <input
                                type="number"
                                value={config.discount.order}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        discount: { ...config.discount, order: Number(e.target.value) },
                                    })
                                }
                            />
                        </label>
                    </fieldset>
                    <fieldset>
                        <legend>Rating Settings</legend>
                        <label>
                            Font Size:
                            <input
                                type="text"
                                value={config.rating.fontSize}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        rating: { ...config.rating, fontSize: e.target.value },
                                    })
                                }
                            />
                        </label>
                        <label>
                            Alignment:
                            <select
                                value={config.rating.align}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        rating: { ...config.rating, align: e.target.value },
                                    })
                                }
                            >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                            </select>
                        </label>
                        <label>
                            Color:
                            <input
                                type="color"
                                value={config.rating.color}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        rating: { ...config.rating, color: e.target.value },
                                    })
                                }
                            />
                        </label>
                        <label>
                            Order:
                            <input
                                type="number"
                                value={config.rating.order}
                                onChange={(e) =>
                                    setConfig({
                                        ...config,
                                        rating: { ...config.rating, order: Number(e.target.value) },
                                    })
                                }
                            />
                        </label>
                    </fieldset>
                    <button onClick={handleSave}>Save Configuration</button>
                </div>
            </div>
        </div>
    );
};

export default CardConfigEditor;
